<template lang="pug">
div
  template(v-if="showMarkerRadius")
    GmapCircle(
      v-if="marker && position"
      :center="position"
      :radius="marker.radius"
      :options="markerCircleOptions"
    )
  //- Markers composition
  template(v-if="showMarker")
    GmapMarker(
      v-if="marker && position"
      :position="position"
      :clickable="isClickable"
      :draggable="isDraggable"
      :icon="icon"
      :z-index="zIndex"
      @click="onClick"
      @mouseover="onHover"
      @mouseout="onHoverOut"
      @dragend="changeMarkerPosition"
    )
    //- Leave a space for any other content, like for example with the new Avatar marker.
    slot
  GmapInfoWindow(
    v-if="hasInfoWindow"
    ref="gmapInfoWindow"
    :options="infoWindowOptions"
    :position="position"
    :opened="showInfoWindow"
    @closeclick="closeInfoWindow"
  )
    div(:class="`app-map-marker-info-window-${marker.id}`")
      slot(name="infoWindow")
  GmapInfoWindow(
    v-if="hasTooltip"
    ref="gmapTooltip"
    :options="tooltipOptions"
    :position="position"
    :opened="showTooltip"
  )
    div(:class="`app-map-marker-tooltip-${marker.id}`")
      slot(name="tooltip")
</template>

<script>
import getIconData from '../utils/get_icon_data';

export default {
  name: 'AppMapMarker',
  props: {
    marker: Object,
    zIndex: Number,
    circleOptions: {
      type: Object,
      default() {
        return {};
      },
    },
    showMarkerRadius: {
      type: Boolean,
      default: false,
    },
    showMarker: {
      type: Boolean,
      default: true,
    },
    showMarkerTooltip: {
      type: Boolean,
      default: false,
    },
    isClickable: {
      type: Boolean,
      default: false,
    },
    isHoverable: {
      type: Boolean,
      default: false,
    },
    isDraggable: {
      type: Boolean,
      default: false,
    },
    hasIcon: {
      type: Boolean,
      default: false,
    },
    iconUrl: String,
    iconSize: {
      type: Object,
      default() {
        return {
          width: 40,
          height: 40,
        };
      },
    },
    hasInfoWindow: {
      type: Boolean,
      default: false,
    },
    hasTooltip: {
      type: Boolean,
      default: false,
    },
    showInfoWindow: {
      type: Boolean,
      default: false,
    },
    infoWindowOptions: {
      type: Object,
      default() {
        return {
          pixelOffset: {
            width: 0,
            height: -35,
          },
        };
      },
    },
    centeredOnCoordinates: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      mapCenterOptions: {
        strokeColor: '#ff942a',
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: '#fcead7',
        fillOpacity: 0.3,
      },
      showTooltip: false,
      tooltipOptions: {
        pixelOffset: {
          width: 0,
          height: -35,
        },
      },
      icon: null,
    };
  },
  computed: {
    markerCircleOptions() {
      return Object.assign({}, this.mapCenterOptions, this.circleOptions);
    },
    position() {
      if (this.marker.position) {
        return this.marker.position;
      }
      if (this.marker.latitude && this.marker.longitude) {
        return {
          lat: parseFloat(this.marker.latitude),
          lng: parseFloat(this.marker.longitude),
        };
      }
      if (this.marker.lat && this.marker.lng) {
        return {
          lat: parseFloat(this.marker.lat),
          lng: parseFloat(this.marker.lng),
        };
      }

      return {};
    },
  },
  watch: {
    iconSize(newSize, oldSize) {
      if (newSize && this.hasIcon && !!this.iconUrl) {
        this.setAvatarUrl(newSize);
        this.adjustTooltipVerticalOffset(newSize);
      }
    },
    showMarkerTooltip: {
      immediate: true,
      async handler(newStatus) {
        // We need to use `setTimeout` instead of `$nextTick`
        // because on immediate change, the $mapPromise is not yet resolved
        setTimeout(() => {
          if (newStatus) {
            this.onHover();
          } else {
            this.onHoverOut();
          }
        }, 100);
      },
    },
    showTooltip(newStatus, oldStatus) {
      if (this.hasTooltip === false || newStatus === false) {
        return;
      }
      this.$refs.gmapTooltip.$mapPromise.then((map) => {
        const mapDiv = map.getDiv();

        this.$nextTick(() => {
          const $iw = $(mapDiv)
            .find('.app-map-marker-tooltip-' + this.marker.id)
            .parents('.gm-style-iw');

          $iw.parents('.gm-style-iw-a').addClass('tooltip-style-iw-a');
          const $closeButton = $iw.find('button[title="Close"]');

          $closeButton.hide();
        });
      });
    },
  },
  created() {
    if (this.hasIcon && this.iconUrl) {
      this.setAvatarUrl();
    }
  },
  async mounted() {
    this.map = await this.$mapPromise;
  },
  methods: {
    onClick() {
      if (this.isClickable === true) {
        this.$emit('click', this.marker);
        this.$emit('input', this.marker);
        if (this.hasInfoWindow) {
          this.$emit('info-window', this.marker.id);
          if (this.hasTooltip) {
            this.showTooltip = false;
          }
        }
      }
    },
    onHover() {
      if (this.isHoverable && this.hasInfoWindow) {
        this.$emit('info-window', this.marker.id);
      }
      if (this.hasTooltip && !this.showInfoWindow) {
        this.showTooltip = true;
      }
    },
    onHoverOut() {
      if (this.isHoverable && this.hasInfoWindow) {
        this.closeInfoWindow();
      }
      if (this.hasTooltip) {
        this.showTooltip = false;
      }
    },
    adjustTooltipVerticalOffset(newSize) {
      if (this.hasTooltip) {
        const verticalOffset = this.centeredOnCoordinates ? newSize.height / 2 : newSize.height;

        this.tooltipOptions.pixelOffset.height = -verticalOffset;
      }
    },
    changeMarkerPosition(event) {
      if (this.isDraggable === true) {
        this.$emit('update', event);
      }
    },
    setAvatarUrl(customSize) {
      this.icon = getIconData(customSize || this.iconSize, this.iconUrl);
    },
    closeInfoWindow() {
      this.$emit('close-info');
    },
  },
};
</script>
